import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import {
  ChevronLeft, ExternalLink, Plus, Trash2, Pencil, Save,
  RefreshCw, Building2, Image as ImageIcon, Trash,
  Crown, Wrench, Users as UsersIcon, User, Mail, Search, UserPlus,
  MapPin, Star, Copy, Check,
} from 'lucide-react';
import supabase, { waitForSession } from '../lib/supabase';
import PhotoUploader from '../components/PhotoUploader';
import { StripeLinkPanel } from './Clubs';
import {
  PageHeader, Section, Badge, Button, EmptyState, Field, Input, Select, Textarea, Checkbox,
  Modal, StatusLine,
} from '../components/ui';

type Role = 'owner' | 'manager' | 'staff';
type Member = {
  id: string;
  club_id: string;
  user_id: string;
  role: Role;
  created_at: string;
  profiles?: { full_name: string | null };
};

const ROLE_BADGES: Record<Role, { tone: 'lime' | 'blue' | 'gray'; icon: any }> = {
  owner:   { tone: 'lime', icon: Crown },
  manager: { tone: 'blue', icon: Wrench },
  staff:   { tone: 'gray', icon: UsersIcon },
};

type Club = {
  id: string;
  name: string;
  address: string | null;
  description: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  stripe_account_id: string | null;
  stripe_onboarding_completed_at: string | null;
  accepts_freelance_coaches: boolean;
  platform_commission_rate: number;
  is_active: boolean;
  logo_url: string | null;
  photos: string[] | null;
};

type Venue = {
  id: string;
  club_id: string;
  name: string;
  slug: string | null;
  address: string | null;
  coordinates: { lat: number; lng: number } | null;
  photos: string[];
  is_primary: boolean;
  is_active: boolean;
  stripe_account_id: string | null;
  stripe_onboarding_completed_at: string | null;
};

type Court = {
  id: string;
  club_id: string;
  venue_id: string;
  name: string;
  surface: string;
  is_indoor: boolean;
  has_lights: boolean;
  hourly_rate_offpeak: number;
  hourly_rate_peak: number;
  currency: string;
  slot_minutes: number;
  is_active: boolean;
  photos: string[] | null;
};

export default function ClubDetail() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [club, setClub] = useState<Club | null>(null);
  const [courts, setCourts] = useState<Court[]>([]);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editingCourt, setEditingCourt] = useState<Court | 'new' | null>(null);
  const [linking, setLinking] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  // load() is wrapped in try/finally so a failed fetch can't leave the
  // page stuck on "Loading…". showSpinner=false is used for in-place
  // refreshes after a mutation — the existing data stays on screen
  // while the new data fetches, so the form doesn't flicker.
  const load = async ({ showSpinner = false }: { showSpinner?: boolean } = {}) => {
    if (!id) return;
    if (showSpinner) setLoading(true);
    setLoadError(null);
    try {
      const [clubRes, courtsRes, venuesRes] = await Promise.all([
        supabase.from('clubs').select('*').eq('id', id).single(),
        supabase.from('courts').select('*').eq('club_id', id).order('name'),
        supabase.from('club_venues').select('id, name, is_primary').eq('club_id', id),
      ]);
      if (clubRes.error) throw clubRes.error;
      setClub(clubRes.data as Club);
      setCourts((courtsRes.data as Court[]) ?? []);
      setVenues((venuesRes.data as Venue[]) ?? []);
    } catch (err: any) {
      setLoadError(err?.message ?? 'Failed to load club.');
    } finally {
      if (showSpinner) setLoading(false);
    }
  };

  useEffect(() => { load({ showSpinner: true }); }, [id]);

  // Mutations: prefer .select('*').single() so the freshly-saved row
  // snaps into local state without a second round-trip; await load()
  // for the cases we still want a full refresh (deletes, RPC-driven
  // mutations) so the UI doesn't show stale data on the next render.
  const patchClub = async (patch: Partial<Club>) => {
    if (!club) return;
    const { data, error } = await supabase
      .from('clubs').update(patch).eq('id', club.id)
      .select('*').single();
    if (error) {
      if ((error as any).code === 'PGRST116') {
        setMsg({ kind: 'error', text: "You don't have permission to edit this club." });
      } else {
        setMsg({ kind: 'error', text: error.message });
      }
      return;
    }
    setClub(data as Club);
  };

  const deleteCourt = async (c: Court) => {
    setMsg(null);
    // Block if any bookings reference this court — court_bookings is
    // NO ACTION, so the DB would reject it; we pre-check for a clearer
    // message than the raw Postgres FK error.
    const { count } = await supabase.from('court_bookings')
      .select('id', { count: 'exact', head: true }).eq('court_id', c.id);
    if ((count ?? 0) > 0) {
      setMsg({
        kind: 'error',
        text: `Can't delete "${c.name}" — ${count} booking(s) still reference it. Cancel or complete them first, or hide the court instead.`,
      });
      return;
    }
    if (!confirm(`Delete court "${c.name}"? This can't be undone.`)) return;
    const { error } = await supabase.from('courts').delete().eq('id', c.id);
    if (error) { setMsg({ kind: 'error', text: error.message }); return; }
    setMsg({ kind: 'success', text: `Deleted ${c.name}.` });
    await load();
  };

  // Don't open Stripe in admin's own browser tab — that lands the
  // *admin* on the KYC form. Surface the link in a modal so admin
  // can hand it to the club owner via email or copy/paste.
  const [stripeLinkUrl, setStripeLinkUrl] = useState<string | null>(null);
  const openStripeOnboarding = async () => {
    if (!club) return;
    setLinking(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        'stripe-connect-onboarding', { body: { clubId: club.id } },
      );
      if (error) {
        setMsg({ kind: 'error', text: (error as any).message ?? 'Could not generate Stripe link' });
        return;
      }
      if (data?.url) setStripeLinkUrl(data.url);
    } finally {
      setLinking(false);
    }
  };

  const refreshStripe = async () => {
    if (!club) return;
    setLinking(true);
    try {
      await supabase.functions.invoke('refresh-stripe-connect', { body: { clubId: club.id } });
      await load();
    } finally {
      setLinking(false);
    }
  };

  if (loading && !club) return <div className="p-8 text-sm text-gray-500">Loading…</div>;
  if (loadError && !club) {
    return (
      <div className="p-8">
        <StatusLine kind="error">
          {loadError} <button className="underline ml-2" onClick={() => load({ showSpinner: true })}>Retry</button>
        </StatusLine>
      </div>
    );
  }
  if (!club) return null;

  return (
    <div className="p-8 max-w-5xl">
      <button
        onClick={() => nav('/clubs')}
        className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-3"
      >
        <ChevronLeft className="w-4 h-4" /> Back to clubs
      </button>

      <PageHeader
        eyebrow="Club"
        title={(
          <span className="flex items-center gap-3">
            {club.logo_url
              ? <img src={club.logo_url} alt="" className="w-10 h-10 rounded-xl object-cover border border-gray-200" />
              : <div className="w-10 h-10 rounded-xl bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-400"><Building2 className="w-5 h-5" /></div>}
            <span>{club.name}</span>
          </span>
        )}
        subtitle={club.address || 'No address set'}
        actions={
          !club.stripe_onboarding_completed_at
            ? <Button icon={ExternalLink} loading={linking} onClick={openStripeOnboarding}>
                {club.stripe_account_id ? 'Continue Stripe KYC' : 'Start Stripe onboarding'}
              </Button>
            : <Button variant="secondary" icon={RefreshCw} onClick={refreshStripe}>Refresh Stripe status</Button>
        }
      />

      {msg && <div className="mb-4"><StatusLine kind={msg.kind}>{msg.text}</StatusLine></div>}

      {/* Details — name, address, description, contacts */}
      <DetailsSection club={club} onSaved={(c) => setClub(c)} />

      {/* Quick facts */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <Info
          label="Status"
          value={
            club.is_active
              ? <Badge tone="green">Live</Badge>
              : <Badge tone="gray">Hidden</Badge>
          }
          action={
            <Button size="sm" variant="secondary"
              onClick={() => patchClub({ is_active: !club.is_active })}>
              {club.is_active ? 'Hide' : 'Go live'}
            </Button>
          }
        />
        <Info
          label="Stripe Connect"
          value={
            club.stripe_onboarding_completed_at ? <Badge tone="green">Connected</Badge>
            : club.stripe_account_id ? <Badge tone="amber">KYC pending</Badge>
            : <Badge tone="gray">Not started</Badge>
          }
        />
        <Info
          label="Freelance coaches"
          value={club.accepts_freelance_coaches
            ? <Badge tone="lime">Open</Badge>
            : <Badge tone="gray">Staff only</Badge>}
          action={
            <Button size="sm" variant="secondary"
              onClick={() => patchClub({ accepts_freelance_coaches: !club.accepts_freelance_coaches })}>
              Toggle
            </Button>
          }
        />
      </div>

      {/* Branding: logo */}
      <Section
        title="Brand"
        subtitle="The logo appears next to the club name on the customer app. A square PNG or SVG (exported as PNG) works best."
      >
        <LogoField
          club={club}
          onChange={(url) => patchClub({ logo_url: url })}
        />
      </Section>

      {/* Team — assign/manage club admins */}
      <TeamSection clubId={club.id} clubName={club.name} />

      {/* Venues — physical locations under this brand */}
      <VenuesSection clubId={club.id} />

      {/* Facility photos */}
      <Section
        title="Facility photos"
        subtitle="Shown on the club detail page. The first image is the club cover, and is also used as the fallback hero image on any court that has no photos of its own."
      >
        <PhotoUploader
          bucket="club-photos"
          pathPrefix={club.id}
          photos={Array.isArray(club.photos) ? club.photos : []}
          onChange={(next) => patchClub({ photos: next })}
          max={8}
        />
      </Section>

      {/* Courts */}
      <Section
        title="Courts"
        subtitle="Add or edit the courts at this facility. Court photos are shown on the customer app."
        actions={<Button icon={Plus} onClick={() => setEditingCourt('new')}>Add court</Button>}
        className="p-0 overflow-hidden"
      >
        {courts.length === 0 ? (
          <EmptyState
            icon={Building2}
            title="No courts yet"
            description="Add your first court so it shows up in search."
            action={<Button icon={Plus} onClick={() => setEditingCourt('new')}>Add a court</Button>}
          />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50/70 text-[11px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Court</th>
                <th className="text-left px-5 py-3 font-semibold">Venue</th>
                <th className="text-left px-5 py-3 font-semibold">Surface</th>
                <th className="text-left px-5 py-3 font-semibold">Rates</th>
                <th className="text-left px-5 py-3 font-semibold">Status</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {courts.map((c) => (
                <tr key={c.id} className="border-t border-gray-100 hover:bg-gray-50/60">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-gray-100 overflow-hidden flex items-center justify-center text-gray-400 shrink-0">
                        {c.photos && c.photos[0]
                          ? <img src={c.photos[0]} alt="" className="w-full h-full object-cover" />
                          : <ImageIcon className="w-4 h-4" />}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{c.name}</div>
                        <div className="text-xs text-gray-500 capitalize">
                          {c.is_indoor ? 'Indoor' : 'Outdoor'}{c.has_lights ? ' · lights' : ''}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-gray-700">
                    {venues.find((v) => v.id === c.venue_id)?.name ?? <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-5 py-3 capitalize text-gray-700">{c.surface}</td>
                  <td className="px-5 py-3">
                    <div>AED {Number(c.hourly_rate_offpeak).toFixed(0)}</div>
                    <div className="text-xs text-gray-500">Peak AED {Number(c.hourly_rate_peak).toFixed(0)}</div>
                  </td>
                  <td className="px-5 py-3">
                    {c.is_active ? <Badge tone="green">Active</Badge> : <Badge tone="gray">Hidden</Badge>}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="secondary" icon={Pencil} onClick={() => setEditingCourt(c)}>
                        Edit
                      </Button>
                      <Button size="sm" variant="danger" icon={Trash2} onClick={() => deleteCourt(c)}>
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      {editingCourt && (
        <CourtForm
          clubId={club.id}
          court={editingCourt === 'new' ? null : editingCourt}
          onClose={() => setEditingCourt(null)}
          onSaved={async () => { setEditingCourt(null); await load(); }}
        />
      )}

      <DeleteClubSection club={club} onDeleted={() => nav('/clubs')} />

      {stripeLinkUrl && (
        <Modal
          title={club.stripe_account_id && !club.stripe_onboarding_completed_at
            ? 'Resume Stripe onboarding'
            : 'Stripe onboarding link ready'}
          subtitle="Send this link to the club owner — they finish KYC themselves."
          onClose={() => setStripeLinkUrl(null)}
        >
          <StripeLinkPanel
            url={stripeLinkUrl}
            clubName={club.name}
            ownerEmail={club.contact_email}
            onDone={() => setStripeLinkUrl(null)}
          />
        </Modal>
      )}
    </div>
  );
}

/**
 * Bottom-of-page Danger zone for irreversible club deletion.
 *
 * Pre-checks the three NO ACTION foreign keys (coach_bookings,
 * court_bookings, stripe_transfers) and refuses with a clear message
 * if any exist — otherwise the raw Postgres FK error would leak.
 *
 * Confirmation requires typing the club's exact display name.
 * On success, calls onDeleted() so the parent can navigate back.
 */
function DeleteClubSection({
  club, onDeleted,
}: { club: Club; onDeleted: () => void }) {
  const [counts, setCounts] = useState<{ coachBookings: number; courtBookings: number; transfers: number } | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const [cb, crb, tr] = await Promise.all([
        supabase.from('coach_bookings').select('id', { count: 'exact', head: true }).eq('club_id', club.id),
        supabase.from('court_bookings').select('id', { count: 'exact', head: true }).eq('club_id', club.id),
        supabase.from('stripe_transfers').select('id', { count: 'exact', head: true }).eq('club_id', club.id),
      ]);
      setCounts({
        coachBookings: cb.count ?? 0,
        courtBookings: crb.count ?? 0,
        transfers: tr.count ?? 0,
      });
    })();
  }, [club.id]);

  const hasBlockers = counts !== null
    && (counts.coachBookings > 0 || counts.courtBookings > 0 || counts.transfers > 0);
  const confirmed = confirmText.trim() === club.name.trim();

  const submit = async () => {
    if (!confirmed) return;
    setBusy(true); setErr(null);
    const { error } = await supabase.from('clubs').delete().eq('id', club.id);
    setBusy(false);
    if (error) { setErr(error.message); return; }
    onDeleted();
  };

  return (
    <>
      <Section
        title={<span className="flex items-center gap-2 text-red-700"><Trash2 className="w-4 h-4" /> Danger zone</span>}
        subtitle="Deleting a club removes its venues, courts, hours, members, and photos in one sweep. Past bookings, payments, and Stripe transfers block the delete — cancel or complete them first."
        className="border-red-200"
      >
        {counts === null ? (
          <div className="text-sm text-gray-500">Checking references…</div>
        ) : hasBlockers ? (
          <div className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg p-3">
            Cannot delete — still referenced by
            {counts.coachBookings > 0 && <> {counts.coachBookings} coach booking(s)</>}
            {counts.coachBookings > 0 && (counts.courtBookings > 0 || counts.transfers > 0) ? ',' : ''}
            {counts.courtBookings > 0 && <> {counts.courtBookings} court booking(s)</>}
            {counts.courtBookings > 0 && counts.transfers > 0 ? ',' : ''}
            {counts.transfers > 0 && <> {counts.transfers} Stripe transfer(s)</>}.
          </div>
        ) : (
          <Button variant="danger" icon={Trash2} onClick={() => { setConfirmText(''); setErr(null); setShowModal(true); }}>
            Delete this club
          </Button>
        )}
      </Section>

      {showModal && (
        <Modal title={`Delete ${club.name}?`} onClose={() => setShowModal(false)}>
          <div className="space-y-3">
            <div className="text-sm text-gray-800">
              This permanently removes the club and cascades to venues, courts, hours, members, rental items, holidays, favorites, coach affiliations, and session packages. <strong>This cannot be undone.</strong>
            </div>
            <Field label={`Type "${club.name}" to confirm`}>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                autoFocus
              />
            </Field>
            {err && <StatusLine kind="error">{err}</StatusLine>}
            <div className="flex gap-2 pt-1">
              <Button type="button" variant="secondary" className="flex-1" onClick={() => setShowModal(false)}>
                Keep the club
              </Button>
              <Button type="button" variant="danger" className="flex-1" icon={Trash2}
                loading={busy} disabled={!confirmed || busy} onClick={submit}>
                Delete forever
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

function DetailsSection({ club, onSaved }: { club: Club; onSaved: (updated: Club) => void }) {
  const [name, setName] = useState(club.name);
  const [address, setAddress] = useState(club.address ?? '');
  const [description, setDescription] = useState(club.description ?? '');
  const [email, setEmail] = useState(club.contact_email ?? '');
  const [phone, setPhone] = useState(club.contact_phone ?? '');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  // Sync local form state whenever the parent supplies a fresh club
  // row — covers two cases: (a) the parent reloaded after another
  // mutation (logo upload, photo edit, status toggle) and the form
  // would otherwise show pre-mutation values; (b) the user navigates
  // between clubs without unmounting (route param change).
  useEffect(() => {
    setName(club.name);
    setAddress(club.address ?? '');
    setDescription(club.description ?? '');
    setEmail(club.contact_email ?? '');
    setPhone(club.contact_phone ?? '');
  }, [
    club.id,
    club.name,
    club.address,
    club.description,
    club.contact_email,
    club.contact_phone,
  ]);

  const dirty =
    name !== club.name
    || address !== (club.address ?? '')
    || description !== (club.description ?? '')
    || email !== (club.contact_email ?? '')
    || phone !== (club.contact_phone ?? '');

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setMsg({ kind: 'error', text: 'Name is required.' }); return; }
    setBusy(true); setMsg(null);
    try {
      const patch = {
        name: name.trim(),
        address: address.trim() || null,
        description: description.trim() || null,
        contact_email: email.trim() || null,
        contact_phone: phone.trim() || null,
      };
      // .select('*').single() so we can hand the freshly-saved row
      // straight back to the parent — that keeps the dirty-state
      // calculation correct without waiting for a follow-up reload.
      const { data, error } = await supabase
        .from('clubs').update(patch).eq('id', club.id)
        .select('*').single();
      if (error) {
        if ((error as any).code === 'PGRST116') {
          setMsg({ kind: 'error', text: "You don't have permission to edit this club." });
        } else {
          setMsg({ kind: 'error', text: error.message });
        }
        return;
      }
      setMsg({ kind: 'success', text: 'Saved.' });
      onSaved(data as Club);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section
      title="Details"
      subtitle="Club name, address, and contact info shown on the customer app."
    >
      <form onSubmit={save} className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Club name" className="sm:col-span-2">
            <Input value={name} onChange={(e) => setName(e.target.value)} required />
          </Field>
          <Field label="Address" className="sm:col-span-2">
            <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Street, area, city" />
          </Field>
          <Field label="Description" className="sm:col-span-2">
            <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="Short blurb shown on the club detail page." />
          </Field>
          <Field label="Contact email">
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="info@club.ae" />
          </Field>
          <Field label="Contact phone">
            <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+971 …" />
          </Field>
        </div>
        {msg && <StatusLine kind={msg.kind}>{msg.text}</StatusLine>}
        <div className="flex justify-end">
          <Button type="submit" icon={Save} loading={busy} disabled={!dirty || busy}>
            Save changes
          </Button>
        </div>
      </form>
    </Section>
  );
}

function Info({
  label, value, action,
}: { label: string; value: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.02)]">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="mt-1 flex items-center justify-between gap-2">
        <div>{value}</div>
        {action}
      </div>
    </div>
  );
}

/**
 * Single-slot logo uploader: stores into the club-photos bucket under the
 * club-id prefix so the same RLS policies apply, and persists the public
 * URL onto clubs.logo_url.
 */
function LogoField({
  club, onChange,
}: {
  club: Club;
  onChange: (url: string | null) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const pick = () => inputRef.current?.click();

  const onFile = async (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    setErr(null);
    setUploading(true);
    try {
      await waitForSession();

      const ext = (file.name.split('.').pop() || 'png').toLowerCase();
      const stamp = Date.now().toString(36);
      const path = `${club.id}/logo-${stamp}.${ext}`;
      const { error } = await supabase.storage
        .from('club-photos')
        .upload(path, file, { cacheControl: '3600', upsert: true, contentType: file.type });
      if (error) { setErr(error.message); return; }
      const { data } = supabase.storage.from('club-photos').getPublicUrl(path);
      onChange(data.publicUrl);
    } catch (e: any) {
      setErr(e?.message ?? 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const remove = async () => {
    // Best-effort: also nuke the object from storage so we don't leak files.
    const marker = '/storage/v1/object/public/club-photos/';
    const idx = club.logo_url?.indexOf(marker);
    if (club.logo_url && idx && idx >= 0) {
      const objectPath = club.logo_url.slice(idx + marker.length);
      try {
        await waitForSession();
        await supabase.storage.from('club-photos').remove([objectPath]);
      } catch {
        // Best-effort delete; keep the UI responsive if storage cleanup fails.
      }
    }
    onChange(null);
  };

  return (
    <div className="flex items-center gap-4">
      <div className="w-24 h-24 rounded-2xl bg-gray-100 border border-gray-200 flex items-center justify-center overflow-hidden shrink-0">
        {club.logo_url
          ? <img src={club.logo_url} alt="" className="w-full h-full object-cover" />
          : <Building2 className="w-7 h-7 text-gray-400" />}
      </div>
      <div className="flex-1">
        <div className="flex gap-2">
          <Button size="sm" onClick={pick} loading={uploading} icon={ImageIcon}>
            {club.logo_url ? 'Replace logo' : 'Upload logo'}
          </Button>
          {club.logo_url && (
            <Button size="sm" variant="danger" icon={Trash} onClick={remove}>
              Remove
            </Button>
          )}
        </div>
        <div className="text-xs text-gray-500 mt-2">
          PNG / JPEG / WebP / SVG-as-PNG up to 10&nbsp;MB. Square images render best.
        </div>
        {err && <div className="text-xs text-red-700 mt-1">{err}</div>}
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/avif,image/svg+xml"
          className="hidden"
          onChange={(e) => onFile(e.target.files)}
        />
      </div>
    </div>
  );
}

/**
 * Court edit modal, with photo uploader. The photos bucket path is
 * <club_id>/<court_id>/... so the Storage RLS match on the first path
 * segment still passes for admins and club managers.
 */
function CourtForm({
  clubId, court, onClose, onSaved,
}: {
  clubId: string;
  court: Court | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = !court;
  const [name, setName] = useState(court?.name ?? '');
  const [surface, setSurface] = useState(court?.surface ?? 'hard');
  const [isIndoor, setIsIndoor] = useState(court?.is_indoor ?? false);
  const [hasLights, setHasLights] = useState(court?.has_lights ?? true);
  const [offpeak, setOffpeak] = useState(String(court?.hourly_rate_offpeak ?? 80));
  const [peak, setPeak] = useState(String(court?.hourly_rate_peak ?? 120));
  const [slot, setSlot] = useState(String(court?.slot_minutes ?? 60));
  const [isActive, setIsActive] = useState(court?.is_active ?? true);
  const [photos, setPhotos] = useState<string[]>(court?.photos ?? []);
  const [courtId, setCourtId] = useState<string | null>(court?.id ?? null);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [venueId, setVenueId] = useState<string>(court?.venue_id ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from('club_venues')
        .select('*')
        .eq('club_id', clubId)
        .eq('is_active', true)
        .order('is_primary', { ascending: false })
        .order('name', { ascending: true });
      const list = (data ?? []) as Venue[];
      setVenues(list);
      // Default a new court to the club's primary venue.
      if (!venueId && list.length > 0) {
        setVenueId(list.find((v) => v.is_primary)?.id ?? list[0].id);
      }
    })();
  }, [clubId]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!venueId) { setErr('Pick a venue for this court.'); return; }
    setBusy(true); setErr(null);
    const payload = {
      club_id: clubId,
      venue_id: venueId,
      name, surface,
      is_indoor: isIndoor, has_lights: hasLights,
      hourly_rate_offpeak: Number(offpeak),
      hourly_rate_peak:    Number(peak),
      slot_minutes:        Number(slot),
      is_active:           isActive,
      photos,
    };
    if (isNew) {
      const { data, error } = await supabase.from('courts').insert(payload).select('id').single();
      setBusy(false);
      if (error) { setErr(error.message); return; }
      setCourtId(data.id);
      onSaved();
    } else {
      const { error } = await supabase.from('courts').update(payload).eq('id', court!.id);
      setBusy(false);
      if (error) { setErr(error.message); return; }
      onSaved();
    }
  };

  const photoPath = courtId ? `${clubId}/${courtId}` : null;

  return (
    <Modal
      title={isNew ? 'Add court' : `Edit ${court?.name}`}
      subtitle={isNew ? 'Create the court first, then come back to attach photos.' : undefined}
      onClose={onClose}
      wide
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Venue"
            className="sm:col-span-2"
            hint={venues.length <= 1
              ? 'Courts belong to a venue. Add more venues to this club to move courts between them.'
              : 'Courts can be reassigned to a different venue at any time.'}>
            <Select value={venueId} onChange={(e) => setVenueId(e.target.value)} required>
              {venues.length === 0 && <option value="">No active venues — add one first</option>}
              {venues.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}{v.is_primary ? ' (primary)' : ''}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Court name" className="sm:col-span-2">
            <Input value={name} onChange={(e) => setName(e.target.value)} required />
          </Field>
          <Field label="Surface">
            <Select value={surface} onChange={(e) => setSurface(e.target.value)} className="capitalize">
              {['hard','clay','grass','carpet','astro-turf'].map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </Select>
          </Field>
          <Field label="Slot length">
            <Select value={slot} onChange={(e) => setSlot(e.target.value)}>
              <option value="30">30 min</option>
              <option value="60">60 min</option>
              <option value="90">90 min</option>
              <option value="120">120 min</option>
            </Select>
          </Field>
          <Field label="Off-peak rate (AED/hr)">
            <Input type="number" min="0" value={offpeak} onChange={(e) => setOffpeak(e.target.value)} required />
          </Field>
          <Field label="Peak rate (AED/hr)">
            <Input type="number" min="0" value={peak} onChange={(e) => setPeak(e.target.value)} required />
          </Field>
        </div>

        <div className="flex flex-wrap gap-4 py-1 border-y border-gray-100">
          <Checkbox checked={isIndoor} onChange={setIsIndoor} label="Indoor" />
          <Checkbox checked={hasLights} onChange={setHasLights} label="Lights" />
          <Checkbox checked={isActive} onChange={setIsActive} label="Visible to customers" />
        </div>

        <div>
          <div className="text-xs font-medium text-gray-700">Court photos</div>
          <div className="text-[11px] text-gray-500 mb-2">
            First image is the hero on the customer court detail page. If you leave this empty, the club's Facility photos are used as a fallback.
          </div>
          {photoPath ? (
            <PhotoUploader
              bucket="court-photos"
              pathPrefix={photoPath}
              photos={photos}
              onChange={async (next) => {
                setPhotos(next);
                // Persist immediately once the court row exists, so uploads
                // aren't lost if the user closes the modal without saving.
                if (!isNew && court) {
                  await supabase.from('courts').update({ photos: next }).eq('id', court.id);
                }
              }}
              max={6}
            />
          ) : (
            <div className="text-xs text-gray-500 bg-gray-50 border border-dashed border-gray-300 rounded-lg p-4">
              Save the court first, then reopen this form to add photos.
            </div>
          )}
        </div>

        {err && <StatusLine kind="error">{err}</StatusLine>}

        <div className="flex gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose} className="flex-1">Cancel</Button>
          <Button type="submit" loading={busy} icon={Save} className="flex-1">
            {isNew ? 'Create court' : 'Save changes'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * List of physical locations under this club brand. Every club has at
 * least one "primary" venue created by the multi-venue migration.
 * Courts, hours (future), closures, and coach affiliations live under a
 * venue. Club keeps brand identity (name, logo, Stripe).
 */
function VenuesSection({ clubId }: { clubId: string }) {
  const [venues, setVenues] = useState<Venue[]>([]);
  const [courtCounts, setCourtCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Venue | 'new' | null>(null);
  const [linkingVenueId, setLinkingVenueId] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  const openStripeOnboarding = async (v: Venue) => {
    setLinkingVenueId(v.id); setMsg(null);
    try {
      const { data, error } = await supabase.functions.invoke('stripe-connect-onboarding', {
        body: { venueId: v.id },
      });
      if (error) { setMsg({ kind: 'error', text: error.message }); return; }
      if (data?.url) window.open(data.url, '_blank', 'noopener,noreferrer');
    } finally {
      setLinkingVenueId(null);
    }
  };

  const refreshStripe = async (v: Venue) => {
    setLinkingVenueId(v.id); setMsg(null);
    try {
      const { error } = await supabase.functions.invoke('refresh-stripe-connect', {
        body: { venueId: v.id },
      });
      if (error) { setMsg({ kind: 'error', text: error.message }); return; }
      setMsg({ kind: 'success', text: 'Stripe status refreshed.' });
      await load();
    } finally {
      setLinkingVenueId(null);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [vRes, cRes] = await Promise.all([
        supabase.from('club_venues')
          .select('*')
          .eq('club_id', clubId)
          .order('is_primary', { ascending: false })
          .order('name', { ascending: true }),
        supabase.from('courts').select('venue_id').eq('club_id', clubId),
      ]);
      setVenues((vRes.data ?? []) as Venue[]);
      const counts: Record<string, number> = {};
      for (const row of (cRes.data ?? []) as Array<{ venue_id: string }>) {
        counts[row.venue_id] = (counts[row.venue_id] ?? 0) + 1;
      }
      setCourtCounts(counts);
    } catch (err: any) {
      setMsg({ kind: 'error', text: err?.message ?? 'Failed to load venues.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [clubId]);

  const setPrimary = async (v: Venue) => {
    if (v.is_primary) return;
    setMsg(null);
    // Flip in two steps: clear old primary, set new. Partial-unique index
    // rejects two primaries; doing the clear first avoids conflict.
    const prior = venues.find((x) => x.is_primary && x.id !== v.id);
    if (prior) {
      const { error: e1 } = await supabase.from('club_venues').update({ is_primary: false }).eq('id', prior.id);
      if (e1) { setMsg({ kind: 'error', text: e1.message }); return; }
    }
    const { error: e2 } = await supabase.from('club_venues').update({ is_primary: true }).eq('id', v.id);
    if (e2) { setMsg({ kind: 'error', text: e2.message }); return; }
    setMsg({ kind: 'success', text: `${v.name} is now the primary venue.` });
    await load();
  };

  const remove = async (v: Venue) => {
    if (v.is_primary) {
      setMsg({ kind: 'error', text: 'Set another venue as primary before deleting this one.' });
      return;
    }
    if ((courtCounts[v.id] ?? 0) > 0) {
      setMsg({ kind: 'error', text: `Can't delete — ${courtCounts[v.id]} court(s) still assigned. Reassign them first.` });
      return;
    }
    if (!confirm(`Delete venue "${v.name}"? This can't be undone.`)) return;
    const { error } = await supabase.from('club_venues').delete().eq('id', v.id);
    if (error) { setMsg({ kind: 'error', text: error.message }); return; }
    setMsg({ kind: 'success', text: 'Venue deleted.' });
    await load();
  };

  return (
    <Section
      title="Venues"
      subtitle="Physical locations of this club. A club can have one or many. Courts, hours and closures belong to a venue."
      actions={<Button icon={Plus} onClick={() => setEditing('new')}>Add venue</Button>}
      className="p-0 overflow-hidden"
    >
      {msg && <div className="px-5 pt-4"><StatusLine kind={msg.kind}>{msg.text}</StatusLine></div>}
      {loading ? (
        <div className="p-8 text-sm text-gray-500">Loading venues…</div>
      ) : venues.length === 0 ? (
        <EmptyState
          icon={MapPin}
          title="No venues yet"
          description="Every club needs at least one venue. Add the first one to surface courts under it."
          action={<Button icon={Plus} onClick={() => setEditing('new')}>Add venue</Button>}
        />
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-gray-50/70 text-[11px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
            <tr>
              <th className="text-left px-5 py-3 font-semibold">Venue</th>
              <th className="text-left px-5 py-3 font-semibold">Address</th>
              <th className="text-left px-5 py-3 font-semibold">Courts</th>
              <th className="text-left px-5 py-3 font-semibold">Stripe</th>
              <th className="text-left px-5 py-3 font-semibold">Status</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {venues.map((v) => (
              <tr key={v.id} className="border-t border-gray-100 hover:bg-gray-50/60">
                <td className="px-5 py-3">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">{v.name}</span>
                    {v.is_primary && <Badge tone="lime" icon={Star}>Primary</Badge>}
                  </div>
                  {v.slug && <div className="text-xs text-gray-500">/{v.slug}</div>}
                </td>
                <td className="px-5 py-3 text-gray-700">{v.address || <span className="text-gray-400">—</span>}</td>
                <td className="px-5 py-3 text-gray-700">{courtCounts[v.id] ?? 0}</td>
                <td className="px-5 py-3">
                  {v.stripe_account_id
                    ? v.stripe_onboarding_completed_at
                        ? <Badge tone="green">Connected</Badge>
                        : <Badge tone="amber">KYC pending</Badge>
                    : <Badge tone="gray">Inherits club</Badge>}
                </td>
                <td className="px-5 py-3">
                  {v.is_active ? <Badge tone="green">Active</Badge> : <Badge tone="gray">Hidden</Badge>}
                </td>
                <td className="px-5 py-3">
                  <div className="flex items-center justify-end gap-1 flex-wrap">
                    {v.stripe_account_id
                      ? v.stripe_onboarding_completed_at
                          ? <Button size="sm" variant="secondary" icon={RefreshCw}
                              loading={linkingVenueId === v.id}
                              onClick={() => refreshStripe(v)}>Refresh Stripe</Button>
                          : <Button size="sm" icon={ExternalLink}
                              loading={linkingVenueId === v.id}
                              onClick={() => openStripeOnboarding(v)}>Continue KYC</Button>
                      : <Button size="sm" variant="secondary" icon={ExternalLink}
                          loading={linkingVenueId === v.id}
                          onClick={() => openStripeOnboarding(v)}>Onboard Stripe</Button>}
                    {!v.is_primary && (
                      <Button size="sm" variant="secondary" icon={Star} onClick={() => setPrimary(v)}>
                        Make primary
                      </Button>
                    )}
                    <Button size="sm" variant="secondary" icon={Pencil} onClick={() => setEditing(v)}>
                      Edit
                    </Button>
                    {!v.is_primary && (
                      <Button size="sm" variant="secondary" icon={Trash2} onClick={() => remove(v)}>
                        Delete
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing && (
        <VenueForm
          clubId={clubId}
          venue={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}
    </Section>
  );
}

function VenueForm({
  clubId, venue, onClose, onSaved,
}: {
  clubId: string;
  venue: Venue | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = !venue;
  const [name, setName] = useState(venue?.name ?? '');
  const [slug, setSlug] = useState(venue?.slug ?? '');
  const [address, setAddress] = useState(venue?.address ?? '');
  const [lat, setLat] = useState(venue?.coordinates?.lat?.toString() ?? '');
  const [lng, setLng] = useState(venue?.coordinates?.lng?.toString() ?? '');
  const [isActive, setIsActive] = useState(venue?.is_active ?? true);
  const [photos, setPhotos] = useState<string[]>(venue?.photos ?? []);
  const [venueId, setVenueId] = useState<string | null>(venue?.id ?? null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setErr('Name is required.'); return; }
    setBusy(true); setErr(null);
    const parsedLat = lat.trim() ? Number(lat) : null;
    const parsedLng = lng.trim() ? Number(lng) : null;
    if ((parsedLat === null) !== (parsedLng === null)) {
      setBusy(false); setErr('Provide both latitude and longitude, or neither.'); return;
    }
    if (parsedLat !== null && (Number.isNaN(parsedLat) || Number.isNaN(parsedLng))) {
      setBusy(false); setErr('Coordinates must be numbers.'); return;
    }
    const payload = {
      club_id: clubId,
      name: name.trim(),
      slug: slug.trim() || null,
      address: address.trim() || null,
      coordinates: parsedLat !== null ? { lat: parsedLat, lng: parsedLng } : null,
      is_active: isActive,
      photos,
    };
    if (isNew) {
      const { data, error } = await supabase.from('club_venues').insert(payload).select('id').single();
      setBusy(false);
      if (error) { setErr(error.message); return; }
      // Keep the modal open so the user can attach photos now that the
      // venue row exists (PhotoUploader needs the id for the path prefix).
      setVenueId(data.id);
      onSaved();
    } else {
      const { error } = await supabase.from('club_venues').update(payload).eq('id', venue!.id);
      setBusy(false);
      if (error) { setErr(error.message); return; }
      onSaved();
    }
  };

  return (
    <Modal
      title={isNew ? 'Add venue' : `Edit ${venue?.name}`}
      subtitle={isNew
        ? 'A venue is one physical location of this club. Courts get assigned to a venue individually.'
        : undefined}
      onClose={onClose}
    >
      <form onSubmit={submit} className="space-y-4">
        <Field label="Name" hint="e.g. Al Barsha, Jumeirah">
          <Input value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
        </Field>
        <Field label="Slug" hint="Optional URL fragment. Leave blank to auto-generate later.">
          <Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="al-barsha" />
        </Field>
        <Field label="Address">
          <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Street, area, city" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Latitude">
            <Input type="number" step="any" value={lat} onChange={(e) => setLat(e.target.value)} placeholder="25.1107" />
          </Field>
          <Field label="Longitude">
            <Input type="number" step="any" value={lng} onChange={(e) => setLng(e.target.value)} placeholder="55.1830" />
          </Field>
        </div>
        <Checkbox checked={isActive} onChange={setIsActive}
          label="Visible to customers"
          hint="Unchecked = venue stays in admin but won't show up in customer search." />

        <div>
          <div className="text-xs font-medium text-gray-700">Facility photos</div>
          <div className="text-[11px] text-gray-500 mb-2">
            First image is the venue cover on the customer app, and is the fallback hero for any court here that has no photos of its own.
          </div>
          {venueId ? (
            <PhotoUploader
              bucket="club-photos"
              pathPrefix={venueId}
              photos={photos}
              onChange={async (next) => {
                setPhotos(next);
                // Persist immediately once the venue row exists, so uploads
                // aren't lost if the user closes the modal without saving.
                await supabase.from('club_venues').update({ photos: next }).eq('id', venueId);
              }}
              max={8}
            />
          ) : (
            <div className="text-xs text-gray-500 border border-dashed border-gray-300 rounded-lg p-3">
              Save the venue first, then you'll be able to attach photos.
            </div>
          )}
        </div>

        {err && <StatusLine kind="error">{err}</StatusLine>}
        <div className="flex gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose} className="flex-1">
            {venueId ? 'Done' : 'Cancel'}
          </Button>
          <Button type="submit" loading={busy} icon={Save} className="flex-1">
            {isNew && !venueId ? 'Create venue' : 'Save changes'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Team management for this club, admin-side. Mirrors what club owners see
 * in the partner app but without the owner-only gate — platform admins
 * can always add/change/remove members here, and every action is written
 * to the admin audit log. The DB trigger still enforces "at least one
 * owner" so we can't accidentally strand a club.
 */
function TeamSection({ clubId, clubName }: { clubId: string; clubName: string }) {
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState<'owner' | 'teammate' | null>(null);
  const [msg, setMsg] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('club_members')
        .select('id, club_id, user_id, role, created_at, profiles!user_id(full_name)')
        .eq('club_id', clubId)
        .order('created_at');
      if (error) throw error;
      setMembers((data ?? []) as unknown as Member[]);
    } catch (err: any) {
      setMsg({ kind: 'error', text: err?.message ?? 'Failed to load team.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [clubId]);

  const hasOwner = members.some((m) => m.role === 'owner');

  const changeRole = async (m: Member, role: Role) => {
    if (role === m.role) return;
    setMsg(null);
    const { error } = await supabase.from('club_members').update({ role }).eq('id', m.id);
    if (error) { setMsg({ kind: 'error', text: error.message }); return; }
    await supabase.rpc('audit_log', {
      p_action: 'club_member_role_changed',
      p_target_type: 'club', p_target_id: clubId,
      p_metadata: {
        club_name: clubName, user_id: m.user_id,
        from_role: m.role, to_role: role,
      } as any,
    });
    setMsg({ kind: 'success', text: `${m.profiles?.full_name ?? 'Member'} is now ${role}.` });
    await load();
  };

  const remove = async (m: Member) => {
    if (!confirm(`Remove ${m.profiles?.full_name ?? 'this member'} from ${clubName}?`)) return;
    setMsg(null);
    const { error } = await supabase.from('club_members').delete().eq('id', m.id);
    if (error) { setMsg({ kind: 'error', text: error.message }); return; }
    await supabase.rpc('audit_log', {
      p_action: 'club_member_removed',
      p_target_type: 'club', p_target_id: clubId,
      p_metadata: {
        club_name: clubName, user_id: m.user_id, role: m.role,
      } as any,
    });
    setMsg({ kind: 'success', text: 'Removed.' });
    await load();
  };

  return (
    <Section
      title="Team"
      subtitle={hasOwner
        ? 'Owners and managers who can run this club in the partner app.'
        : 'This club has no owner yet — nobody can manage it in the partner app until you assign one.'}
      actions={
        <div className="flex gap-2">
          {!hasOwner && (
            <Button icon={UserPlus} onClick={() => setShowAdd('owner')}>
              Assign owner
            </Button>
          )}
          {hasOwner && (
            <Button variant="secondary" icon={Plus} onClick={() => setShowAdd('teammate')}>
              Invite teammate
            </Button>
          )}
        </div>
      }
      className="p-0 overflow-hidden"
    >
      {msg && <div className="p-3 border-b border-gray-100"><StatusLine kind={msg.kind}>{msg.text}</StatusLine></div>}

      {!hasOwner && !loading && (
        <div className="mx-5 mt-4 mb-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900">
          <strong>Action needed:</strong> click <em>Assign owner</em> and search by email.
          The person must already have an AceRent account (customer or partner).
        </div>
      )}

      {loading ? (
        <div className="p-10 text-center text-sm text-gray-500">Loading team…</div>
      ) : members.length === 0 ? (
        <EmptyState
          icon={UsersIcon}
          title="No team members yet"
          description="Assign the first owner so they can manage the club in the partner app."
          action={<Button icon={UserPlus} onClick={() => setShowAdd('owner')}>Assign owner</Button>}
        />
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-gray-50/70 text-[11px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
            <tr>
              <th className="text-left px-5 py-3 font-semibold">Member</th>
              <th className="text-left px-5 py-3 font-semibold">Role</th>
              <th className="text-left px-5 py-3 font-semibold">Joined</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {members.map((m) => {
              const RoleIcon = ROLE_BADGES[m.role].icon;
              return (
                <tr key={m.id} className="border-t border-gray-100 hover:bg-gray-50/60">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-gray-400">
                        <User className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-gray-900 truncate">
                          {m.profiles?.full_name ?? '—'}
                        </div>
                        <div className="text-xs text-gray-500 font-mono">
                          {m.user_id.slice(0, 8)}…
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <Badge tone={ROLE_BADGES[m.role].tone} icon={RoleIcon}>{m.role}</Badge>
                  </td>
                  <td className="px-5 py-3 text-xs text-gray-600">
                    {new Date(m.created_at).toLocaleDateString('en-GB', {
                      day: '2-digit', month: 'short', year: 'numeric',
                    })}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex gap-2 justify-end">
                      <Select
                        value={m.role}
                        onChange={(e) => changeRole(m, e.target.value as Role)}
                        className="w-36 h-8 text-xs"
                      >
                        <option value="owner">Owner</option>
                        <option value="manager">Manager</option>
                        <option value="staff">Staff</option>
                      </Select>
                      <Button size="sm" variant="danger" icon={Trash2}
                        onClick={() => remove(m)}>Remove</Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {showAdd && (
        <InviteForm
          clubId={clubId}
          clubName={clubName}
          defaultRole={showAdd === 'owner' ? 'owner' : 'manager'}
          firstOwner={showAdd === 'owner' && !hasOwner}
          onClose={() => setShowAdd(null)}
          onSaved={async () => { setShowAdd(null); await load(); }}
        />
      )}
    </Section>
  );
}

function InviteForm({
  clubId, clubName, defaultRole, firstOwner, onClose, onSaved,
}: {
  clubId: string;
  clubName: string;
  defaultRole: Role;
  firstOwner: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>(defaultRole);
  const [fullName, setFullName] = useState('');
  const [lookup, setLookup] = useState<{ user_id: string; email: string; full_name: string | null } | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [createMode, setCreateMode] = useState<'password' | 'invite'>('password');
  const [createdResult, setCreatedResult] = useState<{
    email: string;
    oneTimePassword: string | null;
    inviteUrl: string | null;
  } | null>(null);
  const [copied, setCopied] = useState(false);
  const [searching, setSearching] = useState(false);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const find = async () => {
    if (!email.trim()) return;
    setSearching(true); setErr(null); setNotFound(false);
    const { data, error } = await supabase.rpc('resolve_user_by_email', { p_email: email.trim() });
    setSearching(false);
    if (error) { setErr(error.message); setLookup(null); return; }
    const row = (data as any[])?.[0];
    if (!row) {
      setNotFound(true);
      setLookup(null);
    } else {
      setLookup(row);
    }
  };

  const createNewUser = async () => {
    setCreating(true); setErr(null);
    const { data, error } = await supabase.functions.invoke('admin-create-user', {
      body: {
        email: email.trim(),
        fullName: fullName.trim() || null,
        mode: createMode,
        clubId,
        role,
      },
    });
    setCreating(false);
    if (error) {
      // Try to pull the server-side error message out of the non-2xx body.
      let serverMsg = error.message;
      try {
        const ctx: any = (error as any).context;
        if (ctx && typeof ctx.json === 'function') {
          const body = await ctx.json();
          if (body?.error) serverMsg = body.error;
        }
      } catch { /* fall through */ }
      setErr(serverMsg);
      return;
    }
    setCreatedResult({
      email: (data as any).email,
      oneTimePassword: (data as any).oneTimePassword ?? null,
      inviteUrl: (data as any).inviteUrl ?? null,
    });
    setNotFound(false);
  };

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!lookup) { await find(); return; }
    setBusy(true); setErr(null);
    const { error } = await supabase.from('club_members').insert({
      club_id: clubId,
      user_id: lookup.user_id,
      role,
    });
    if (error) {
      setBusy(false);
      if (error.message.toLowerCase().includes('duplicate key')) {
        setErr('This user is already a member of the club.');
      } else {
        setErr(error.message);
      }
      return;
    }
    await supabase.rpc('audit_log', {
      p_action: firstOwner ? 'club_owner_assigned' : 'club_member_added',
      p_target_type: 'club', p_target_id: clubId,
      p_metadata: {
        club_name: clubName,
        user_id: lookup.user_id,
        email: lookup.email,
        role,
      } as any,
    });
    setBusy(false);
    onSaved();
  };

  // Success panel after creating a new user — admin sees the one-time
  // password or invite link exactly once.
  if (createdResult) {
    const isPassword = !!createdResult.oneTimePassword;
    const secret = createdResult.oneTimePassword ?? createdResult.inviteUrl ?? '';
    return (
      <Modal
        title={isPassword ? 'User created — share the password' : 'Invite sent'}
        subtitle={isPassword
          ? "This one-time password is shown only once. Copy it and send it to the user through a trusted channel. They'll be prompted to change it after signing in."
          : "We've sent an invite email. If email delivery is unreliable, you can copy the fallback link below and send it directly."}
        onClose={onClose}
      >
        <div className="space-y-4">
          <div className="rounded-lg bg-[#c6f806]/15 border border-[#2d5016]/20 p-3 text-sm">
            {createdResult.email} was added to {clubName} as <strong>{role}</strong>.
          </div>
          <Field label={isPassword ? 'One-time password' : 'Invite link'}>
            <div className="flex gap-2">
              <Input readOnly value={secret} className="font-mono text-xs" onFocus={(e) => e.currentTarget.select()} />
              <Button type="button" variant="secondary" icon={copied ? Check : Copy} onClick={() => copy(secret)}>
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
          </Field>
          {isPassword && (
            <div className="text-xs text-gray-500">
              Tell the user to sign in at the app they'll use (admin, partner, or customer) and then change the password in <em>Settings → Change password</em>.
            </div>
          )}
          <Button type="button" className="w-full" onClick={() => { setCreatedResult(null); onSaved(); }}>
            Done
          </Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title={firstOwner ? 'Assign club owner' : 'Invite a teammate'}
      subtitle={firstOwner
        ? 'The owner will manage this club in the partner app. Find an existing user, or create one below.'
        : 'Search by email. If they do not have an AceRent account yet, you can create one in the same step.'}
      onClose={onClose}
    >
      <form onSubmit={submit} className="space-y-4">
        <Field label="Email">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Mail className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                type="email" value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setLookup(null); setNotFound(false); setErr(null);
                }}
                placeholder="owner@example.com"
                className="pl-9"
                autoFocus
              />
            </div>
            <Button type="button" variant="secondary" icon={Search}
              loading={searching} onClick={find}>Find</Button>
          </div>
        </Field>

        {lookup && (
          <div className="rounded-lg bg-[#c6f806]/15 border border-[#2d5016]/20 p-3 text-sm">
            Found <strong>{lookup.full_name ?? lookup.email}</strong>
            {lookup.full_name && <span className="text-gray-500"> · {lookup.email}</span>}
          </div>
        )}

        <Field label="Role">
          <Select
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
            disabled={firstOwner}
          >
            <option value="owner">Owner — can manage everything, including team</option>
            <option value="manager">Manager — can edit the club, courts, schedule</option>
            <option value="staff">Staff — view-only on most pages</option>
          </Select>
          {firstOwner && (
            <div className="text-xs text-gray-500 mt-1">
              First assignment must be an owner. Additional teammates can be added with any role after this.
            </div>
          )}
        </Field>

        {notFound && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-3">
            <div className="text-sm text-amber-900">
              No AceRent user with that email yet. Create one and add them to {clubName} as <strong>{role}</strong>.
            </div>
            <Field label="Full name (optional)">
              <Input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Alia Tariq" />
            </Field>
            <Field label="How should they sign in first?">
              <div className="grid grid-cols-2 gap-2">
                <label className={`flex items-start gap-2 rounded-md border p-2 text-xs cursor-pointer ${createMode === 'password' ? 'border-[#2d5016] bg-white' : 'border-gray-200 bg-white/40'}`}>
                  <input type="radio" name="createMode" checked={createMode === 'password'}
                    onChange={() => setCreateMode('password')} className="mt-0.5" />
                  <div>
                    <div className="font-medium text-gray-900">One-time password</div>
                    <div className="text-gray-500">Shown once in this dialog. You send it to them.</div>
                  </div>
                </label>
                <label className={`flex items-start gap-2 rounded-md border p-2 text-xs cursor-pointer ${createMode === 'invite' ? 'border-[#2d5016] bg-white' : 'border-gray-200 bg-white/40'}`}>
                  <input type="radio" name="createMode" checked={createMode === 'invite'}
                    onChange={() => setCreateMode('invite')} className="mt-0.5" />
                  <div>
                    <div className="font-medium text-gray-900">Email invite</div>
                    <div className="text-gray-500">Supabase sends a magic link they click to set their password.</div>
                  </div>
                </label>
              </div>
            </Field>
            <Button type="button" icon={UserPlus} loading={creating} onClick={createNewUser} className="w-full">
              {createMode === 'password' ? 'Create user with one-time password' : 'Send email invite'}
            </Button>
          </div>
        )}

        {err && <StatusLine kind="error">{err}</StatusLine>}

        <div className="flex gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose} className="flex-1">Cancel</Button>
          <Button type="submit" loading={busy} icon={Save} className="flex-1" disabled={!lookup}>
            {firstOwner ? 'Assign as owner' : 'Add to club'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
